import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone-esm';
import { useEnvironment } from '../../hooks/env';
import { Attachment } from '../Attachments';
import { Uploader } from './Uploader';
import { UploadingProgressBar } from './UploadingProgressBar';

type UploadAttachmentProps = {
  attachment: Attachment;
  onRemove: () => void;
  onUploaded: () => void;
};

const UploadAttachment: FunctionComponent<UploadAttachmentProps> = ({
  attachment,
  onRemove,
  onUploaded,
}) => {
  const [progress, setProgress] = useState(0);
  const { directUploadsUrl } = useEnvironment();
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  useEffect(() => {
    const uploader = new Uploader(attachment, directUploadsUrl, setProgress);
    uploader.upload((blob) => {
      attachment.blob = blob;
      onUploadedRef.current();
    });
  }, [attachment, directUploadsUrl]);

  return (
    <li className="col-span-1 flex rounded-md shadow-xs">
      <div className="bg-grey-200 text-grey-700 mt-2 flex w-full justify-between rounded-md px-4 py-2">
        {attachment.name}
        <div className="flex justify-end items-center w-full gap-4">
          <UploadingProgressBar
            fileName={attachment.name}
            progress={progress * 100}
          />
          <button type="button" className="px-1" onClick={onRemove}>
            <XMarkIcon className="h-5" />
          </button>
        </div>
      </div>
    </li>
  );
};

type AttachmentUploaderFieldProps = {
  name: string;
  label?: string;
  placeholder?: string;
  value: Attachment[];
  onChange: (attachments: Attachment[]) => void;
  error?: string;
  uploadBoxClassName?: string;
};

export const AttachmentUploaderField: FunctionComponent<
  AttachmentUploaderFieldProps
> = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  error,
  uploadBoxClassName,
}) => {
  const valueRef = useRef(value);
  valueRef.current = value;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    onDrop: (acceptedFiles: Attachment[]) => {
      acceptedFiles.forEach((file) => (file.key = crypto.randomUUID()));
      onChange([...value, ...acceptedFiles]);
    },
  });

  return (
    <div>
      {label && (
        <label
          htmlFor={name}
          className="text-grey-500 mb-0 text-sm font-medium"
        >
          {label}
        </label>
      )}
      <div
        {...getRootProps()}
        className={clsx(
          'border-grey-500 bg-grey-50 flex h-12 flex-col justify-center rounded-lg border-2 border-dashed p-1 text-center text-sm',
          uploadBoxClassName,
          isDragActive && 'border-green-500 text-green-500',
          error && 'border-red-600',
        )}
      >
        <div className="text-grey-700 text-sm font-medium">{placeholder}</div>
        <input {...getInputProps()} id={name} data-testid="dropzone" />
      </div>
      {error && <p className="text-red-600 mt-2 text-sm">{error}</p>}
      <ul>
        {value.map((attachment) => (
          <div key={attachment.key ?? attachment.name}>
            <UploadAttachment
              attachment={attachment}
              onRemove={() => onChange(value.filter((a) => a !== attachment))}
              onUploaded={() => onChange([...valueRef.current])}
            />
          </div>
        ))}
      </ul>
    </div>
  );
};
